// Core imports
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Component imports
import { Button, Col, Form, Input, message, Modal, Popconfirm, Row, Select, Skeleton, Space, Table, Tag, Tooltip, Typography } from 'antd';
import { DeleteOutlined, EditOutlined, LogoutOutlined, PlusOutlined, SaveOutlined, SendOutlined } from '@ant-design/icons';
import SEO from '../components/SEO/SEO';

// Misc imports
import api from '../api';
import { blogAuthActions } from '../store/blog/blogAuth';
import { API_URL, NAV_BLOG, NAV_BLOG_EDITOR_EDIT, NAV_BLOG_EDITOR_NEW, NAV_BLOG_LOGIN } from '../constants';

const { Title, Paragraph } = Typography;

// ─── Quill toolbar configuration ────────────────────────────────────────────

const buildQuillModules = (imageHandler) => ({
  toolbar: {
    container: [
      [{ header: [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      ['blockquote', 'code-block'],
      [{ list: 'ordered' }, { list: 'bullet' }],
      [{ indent: '-1' }, { indent: '+1' }],
      ['link', 'image'],
      ['clean']
    ],
    handlers: { image: imageHandler }
  }
});

const QUILL_FORMATS = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'blockquote', 'code-block', 'list', 'bullet', 'indent',
  'link', 'image'
];

// ─── Slug generator ───────────────────────────────────────────────────────────

const toSlug = (text) =>
  text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 80);

// ─── Post form (create/edit) ─────────────────────────────────────────────────

const PostForm = ({ postId, onSaved }) => {
  const [form] = Form.useForm();
  const quillRef = useRef(null);
  const [content, setContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [loadingPost, setLoadingPost] = useState(!!postId);
  const [existingPost, setExistingPost] = useState(null);

  useEffect(() => {
    if (!postId) {
      return;
    }
    setLoadingPost(true);
    api.fetchMyBlogPosts({ take: 100 })
      .then(result => {
        const found = result.items.find(p => p.id === Number(postId));
        if (found) {
          setExistingPost(found);
          form.setFieldsValue({
            title: found.title,
            slug: found.slug,
            excerpt: found.excerpt || '',
            coverImageUrl: found.coverImageUrl || '',
            tags: found.tags?.map(t => t.name) || []
          });
          setContent(found.content || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoadingPost(false));
  }, [postId]);

  const imageHandler = useCallback(() => {
    const input = document.createElement('input');
    input.setAttribute('type', 'file');
    input.setAttribute('accept', 'image/*');
    input.click();

    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) {
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      try {
        const result = await api.uploadBlogImage({ formData });
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const range = quill.getSelection(true);
          quill.insertEmbed(range.index, 'image', `${API_URL}${result.url}`);
          quill.setSelection(range.index + 1);
        }
      } catch (_) {
        message.error('Image upload failed. Please try again.');
      }
    };
  }, []);

  const modules = buildQuillModules(imageHandler);

  const handleTitleBlur = () => {
    const title = form.getFieldValue('title');
    const currentSlug = form.getFieldValue('slug');
    if (title && !currentSlug) {
      form.setFieldValue('slug', toSlug(title));
    }
  };

  const savePost = async (status) => {
    try {
      const values = await form.validateFields();
      const payload = {
        title: values.title,
        slug: values.slug,
        excerpt: values.excerpt || undefined,
        content,
        coverImageUrl: values.coverImageUrl || undefined,
        status,
        tags: values.tags || []
      };

      if (status === 'published') {
        setPublishing(true);
      } else {
        setSaving(true);
      }

      if (existingPost) {
        await api.updateBlogPost({ id: existingPost.id, body: payload });
        message.success(status === 'published' ? 'Post published!' : 'Draft saved.');
      } else {
        const created = await api.createBlogPost({ body: payload });
        message.success(status === 'published' ? 'Post published!' : 'Draft saved.');
        if (onSaved) {
          onSaved(created);
        }
      }
    } catch (err) {
      if (err?.errorFields) {
        return;
      } // form validation error, do nothing
      message.error(err?.description || 'Could not save post.');
    } finally {
      setSaving(false);
      setPublishing(false);
    }
  };

  if (loadingPost) {
    return <div className="d-flex-center" style={{ minHeight: 300 }}>Loading post…</div>;
  }

  return (
    <Form form={form} layout="vertical" requiredMark={false}>
      <div className="blog-editor-form-header">
        <Title level={4} style={{ marginBottom: 0 }}>
          {existingPost ? `Editing: ${existingPost.title}` : 'New post'}
        </Title>
      </div>
      <Row gutter={[24, 0]}>
        <Col xs={24} lg={16}>
          <Form.Item
            label={<span className="blog-editor-field-label">Title</span>}
            name="title"
            rules={[{ required: true, message: 'Title is required' }]}
          >
            <Input
              size="large"
              placeholder="Post title"
              onBlur={handleTitleBlur}
            />
          </Form.Item>

          <Form.Item
            label={<span className="blog-editor-field-label">Content</span>}
          >
            <div className="blog-editor-quill">
              <ReactQuill
                ref={quillRef}
                theme="snow"
                value={content}
                onChange={setContent}
                modules={modules}
                formats={QUILL_FORMATS}
                placeholder="Write your post here…"
              />
            </div>
          </Form.Item>
        </Col>

        <Col xs={24} lg={8}>
          <Form.Item
            label={<span className="blog-editor-field-label">Slug</span>}
            name="slug"
            rules={[
              { required: true, message: 'Slug is required' },
              { pattern: /^[a-z0-9-]+$/, message: 'Use lowercase letters, numbers and hyphens only' }
            ]}
          >
            <Input placeholder="my-post-slug" addonBefore="/blog/" />
          </Form.Item>

          <Form.Item
            label={<span className="blog-editor-field-label">Excerpt</span>}
            name="excerpt"
          >
            <Input.TextArea
              rows={3}
              placeholder="A short description shown in post cards and search results…"
              maxLength={300}
              showCount
            />
          </Form.Item>

          <Form.Item
            label={<span className="blog-editor-field-label">Cover image URL</span>}
            name="coverImageUrl"
            extra="Recommended: 2100 × 900 px (21:9). Wider images work too, just avoid portrait/square crops."
          >
            <Input placeholder="https://…" />
          </Form.Item>

          <Form.Item
            label={<span className="blog-editor-field-label">Tags</span>}
            name="tags"
          >
            <Select
              mode="tags"
              placeholder="Type a tag and press Enter"
              tokenSeparators={[',']}
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Space direction="vertical" style={{ width: '100%' }}>
              <Button
                icon={<SaveOutlined />}
                block
                onClick={() => savePost('draft')}
                loading={saving}
              >
                Save draft
              </Button>
              <Button
                type="primary"
                icon={<SendOutlined />}
                block
                onClick={() => savePost('published')}
                loading={publishing}
              >
                {existingPost?.status === 'published' ? 'Update & publish' : 'Publish'}
              </Button>
            </Space>
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

PostForm.propTypes = {
  postId: PropTypes.number,
  onSaved: PropTypes.func
};

// ─── Posts list ───────────────────────────────────────────────────────────────

const MyPostsList = ({ onEdit, refreshKey }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    api.fetchMyBlogPosts({ take: 50 })
      .then(result => setPosts(result.items))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const handleDelete = async (id) => {
    try {
      await api.deleteBlogPost({ id });
      message.success('Post deleted.');
      load();
    } catch (err) {
      message.error(err?.description || 'Could not delete post.');
    }
  };

  const columns = [
    {
      title: 'Title',
      dataIndex: 'title',
      render: (text, record) => (
        <span
          className="cursor-pointer"
          onClick={() => navigate(`/blog/${record.slug}`)}
        >
          {text}
        </span>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'published' ? 'success' : 'default'}>
          {status}
        </Tag>
      )
    },
    {
      title: 'Published',
      dataIndex: 'publishedAt',
      width: 140,
      render: (date) => date
        ? new Date(date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
        : '—'
    },
    {
      title: '',
      key: 'actions',
      width: 90,
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              icon={<EditOutlined />}
              size="small"
              onClick={() => onEdit(record.id)}
            />
          </Tooltip>
          <Popconfirm
            title="Delete this post?"
            onConfirm={() => handleDelete(record.id)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Tooltip title="Delete">
              <Button icon={<DeleteOutlined />} size="small" danger />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ];

  return (
    <div className="blog-editor-posts-table">
      <Title level={4} style={{ marginBottom: 16 }}>Your posts</Title>
      <Table
        dataSource={posts}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="small"
      />
    </div>
  );
};

MyPostsList.propTypes = {
  onEdit: PropTypes.func.isRequired,
  refreshKey: PropTypes.number
};

// ─── Profile modal ────────────────────────────────────────────────────────────

const ProfileModal = ({ open, onClose }) => {
  const dispatch = useDispatch();
  const { author } = useSelector(state => state.blogAuth);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    setLoadingProfile(true);
    // Try the authenticated profile endpoint first; fall back to the public author endpoint
    const fetchProfile = author?.slug
      ? api.fetchMyBlogProfile().catch(() => api.fetchBlogAuthor({ slug: author.slug }))
      : api.fetchMyBlogProfile();
    fetchProfile
      .then(profile => {
        form.setFieldsValue({
          name: profile.name || '',
          slug: profile.slug || '',
          bio: profile.bio || '',
          pictureUrl: profile.pictureUrl || '',
          linkedinUrl: profile.linkedinUrl || '',
          websiteUrl: profile.websiteUrl || '',
          facebookUrl: profile.facebookUrl || '',
          xUrl: profile.xUrl || ''
        });
      })
      .catch(() => {
        // Last resort: use the Redux auth state
        form.setFieldsValue({
          name: author?.name || '',
          slug: author?.slug || '',
          bio: author?.bio || '',
          pictureUrl: author?.pictureUrl || '',
          linkedinUrl: author?.linkedinUrl || '',
          websiteUrl: author?.websiteUrl || '',
          facebookUrl: author?.facebookUrl || '',
          xUrl: author?.xUrl || ''
        });
      })
      .finally(() => setLoadingProfile(false));
  }, [open]);

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const result = await api.updateBlogAuthorProfile({ body: values });
      dispatch(blogAuthActions.updateAuthorProfile(result));
      message.success('Profile updated.');
      onClose();
    } catch (err) {
      message.error(err?.description || 'Could not update profile.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title="Edit profile"
      open={open}
      onCancel={onClose}
      footer={null}
      destroyOnClose
    >
      {loadingProfile ? (
        <Skeleton active paragraph={{ rows: 5 }} />
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item label="Name" name="name" rules={[{ required: true, message: 'Name is required' }]}>
            <Input placeholder="Your display name" />
          </Form.Item>
          <Form.Item
            label="Slug"
            name="slug"
            rules={[
              { required: true, message: 'Slug is required' },
              { pattern: /^[a-z0-9-]+$/, message: 'Use lowercase letters, numbers and hyphens only' }
            ]}
          >
            <Input placeholder="your-name" addonBefore="/blog/author/" />
          </Form.Item>
          <Form.Item label="Bio" name="bio">
            <Input.TextArea rows={4} placeholder="Tell readers about yourself…" />
          </Form.Item>
          <Form.Item label="Profile picture URL" name="pictureUrl">
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item label="LinkedIn URL" name="linkedinUrl">
            <Input placeholder="https://linkedin.com/in/…" />
          </Form.Item>
          <Form.Item label="Facebook URL" name="facebookUrl">
            <Input placeholder="https://facebook.com/…" />
          </Form.Item>
          <Form.Item label="X (Twitter) URL" name="xUrl">
            <Input placeholder="https://x.com/…" />
          </Form.Item>
          <Form.Item label="Website" name="websiteUrl">
            <Input placeholder="https://…" />
          </Form.Item>
          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={onClose}>Cancel</Button>
              <Button type="primary" htmlType="submit" loading={saving}>Save</Button>
            </Space>
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

ProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired
};

// ─── Main editor screen ───────────────────────────────────────────────────────

const BlogEditorScreen = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id: postIdParam } = useParams();
  const { author, token } = useSelector(state => state.blogAuth);
  const [editingPostId, setEditingPostId] = useState(postIdParam ? Number(postIdParam) : null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [profileOpen, setProfileOpen] = useState(false);

  // Guard: require auth — use <Navigate> to avoid setState-during-render
  if (!token || !author) {
    return <Navigate to={NAV_BLOG_LOGIN} replace />;
  }

  const handleNewPost = () => {
    setEditingPostId(null);
    navigate(NAV_BLOG_EDITOR_NEW);
  };

  const handleEditPost = (id) => {
    setEditingPostId(id);
    navigate(NAV_BLOG_EDITOR_EDIT.replace(':id', id));
  };

  const handlePostSaved = (created) => {
    setEditingPostId(created.id);
    navigate(NAV_BLOG_EDITOR_EDIT.replace(':id', created.id));
    setRefreshKey(k => k + 1);
  };

  const handleLogout = () => {
    dispatch(blogAuthActions.logout());
    navigate(NAV_BLOG);
  };

  return (
    <>
      <SEO title="Blog Editor" noIndex />

      <div className="landing-container blog-editor-page">
        {/* Toolbar */}
        <div className="blog-editor-toolbar">
          <div>
            <Title level={2} style={{ marginBottom: 2 }}>Blog Editor</Title>
            <Paragraph className="text-color-black-60 mb-0">
              Welcome, {author.name}
            </Paragraph>
          </div>

          <div className="blog-editor-actions">
            <Button onClick={handleNewPost} icon={<PlusOutlined />}>
              New post
            </Button>
            <Button onClick={() => setProfileOpen(true)}>
              Edit profile
            </Button>
            <Tooltip title="Sign out">
              <Button icon={<LogoutOutlined />} onClick={handleLogout} />
            </Tooltip>
          </div>
        </div>

        {/* Posts list */}
        <MyPostsList onEdit={handleEditPost} refreshKey={refreshKey} />

        {/* Post form */}
        <PostForm
          key={editingPostId ?? 'new'}
          postId={editingPostId}
          onSaved={handlePostSaved}
        />
      </div>

      {/* Profile modal */}
      <ProfileModal
        open={profileOpen}
        onClose={() => setProfileOpen(false)}
      />
    </>
  );
};

export default BlogEditorScreen;
